"use client";

import Image from "next/image";
import { ArrowDown, ArrowLeft, ArrowRight, Square, X } from "lucide-react";
import { Fragment, useId, useState } from "react";

import {
  DATA_LABEL_CLS,
  DISPLAY,
  EDITORIAL_SECTION_CLS,
  GROUND,
  Grain,
  INK,
  LABEL_CLS,
  SECTION_INTRO_CLS,
  SECTION_TITLE_CLS,
  SectionMarker,
  SURFACE_DARK,
} from "@/components/editorial";
import { CSS as BRAND_CSS } from "@/components/editorial/engravings/brand";
import { F as BrandFigures } from "@/components/editorial/engravings/brand-figures";
import { Engraving } from "@/components/editorial/engravings/engraving";
import { FEATURE_ROWS, TWIN_PLANS } from "@/app/pricing/pricing-data";

import type { ConceptGroup } from "../spec";
import { ProposalInboxExhibit } from "./govern";

/* ================================================================= */
/*  Shared tone classes                                                */
/* ================================================================= */

const INK_MUTED = "text-[color-mix(in_srgb,var(--theme-ink)_66%,transparent)]";
const INK_80 = "text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";
const GROUND_80 =
  "text-[color-mix(in_srgb,var(--theme-ground)_80%,transparent)]";
const GROUND_88 =
  "text-[color-mix(in_srgb,var(--theme-ground)_88%,transparent)]";

/** Exhibit header row — DATA_LABEL pair over a dotted rule. Mirrors the
 *  anatomy of GovernedAnswerSpecimen (dark) and WaitlistDialog (cream). */
function ExhibitHeader({
  left,
  right,
  dark = false,
}: {
  left: string;
  right: string;
  dark?: boolean;
}) {
  return (
    <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-md:flex-col max-md:items-start max-md:gap-1">
      <span className={`${dark ? GROUND_80 : INK_80} ${DATA_LABEL_CLS}`}>
        {left}
      </span>
      <strong
        className={`${dark ? "text-[var(--theme-accent)]" : "text-[var(--theme-accent-text)]"} ${DATA_LABEL_CLS}`}
      >
        {right}
      </strong>
    </div>
  );
}

/** Ledger row — dt tracked uppercase at 80%, dd in type-data uppercase. */
function LedgerRow({
  label,
  value,
  dark = false,
  emphasis = false,
}: {
  label: string;
  value: string;
  dark?: boolean;
  emphasis?: boolean;
}) {
  const valueTone = dark
    ? emphasis
      ? "text-[color-mix(in_srgb,var(--theme-ground)_92%,transparent)]"
      : GROUND_80
    : emphasis
      ? "text-[var(--theme-ink)]"
      : INK_88;
  return (
    <div className="grid min-h-8 grid-cols-[minmax(9.5rem,36%)_minmax(0,1fr)] border-t border-dotted border-[var(--rule)]">
      <dt
        className={`flex items-center border-r border-dotted border-[var(--rule)] px-3 py-2 text-[length:var(--type-label-small)] font-medium leading-tight tracking-[0.16em] uppercase ${dark ? GROUND_80 : INK_80}`}
      >
        {label}
      </dt>
      <dd
        className={`m-0 flex items-center px-4 py-2 text-[length:var(--type-data)] font-medium tracking-[0.04em] uppercase ${valueTone}`}
      >
        {value}
      </dd>
    </div>
  );
}

/* ================================================================= */
/*  GOV-001 · Control ledger with gate diagram                          */
/* ================================================================= */

const CONTROLS = [
  {
    n: "01",
    title: "Read-only, scoped connectors",
    body: "Connectors read from the systems you approve and nothing else. Those source systems remain authoritative.",
  },
  {
    n: "02",
    title: "Organisation-scoped target",
    body: "Each MLX target is dedicated to your organisation and scoped to it, in an EU or US region.",
  },
  {
    n: "03",
    title: "Human review before publish",
    body: "Your people review what MLX proposes. Nothing becomes business context until they approve it.",
  },
  {
    n: "04",
    title: "Versioned, evidence-linked definitions",
    body: "Each approved definition is linked to its sources and recorded as a version your AI tools can name.",
  },
  {
    n: "05",
    title: "Unsupported questions return “not supported”",
    body: "If the approved product cannot support a question, the Twin says so instead of producing a plausible guess.",
  },
] as const;

/** Five gates on a hairline. Decorative — the numbered list carries the
 *  meaning. An HTML rail, so the gates keep their size at every width and
 *  every stroke reads the theme vars for the palette picker. */
function GateRail() {
  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <span className={`${INK_80} ${DATA_LABEL_CLS}`}>Evidence in</span>
        <span className={`${INK_80} ${DATA_LABEL_CLS}`}>
          Governed answer out
        </span>
      </div>
      <div aria-hidden="true" className="mt-3 flex items-center">
        <span className="h-4 w-px shrink-0 bg-[color-mix(in_srgb,var(--theme-ink)_60%,transparent)]" />
        {CONTROLS.map((control) => (
          <Fragment key={control.n}>
            <span className="h-0 min-w-2 flex-1 border-t border-[var(--rule)]" />
            <span
              className="flex size-8 shrink-0 items-center justify-center border border-[color-mix(in_srgb,var(--theme-ink)_70%,transparent)] bg-[var(--theme-ground)] text-[0.8rem] leading-none font-semibold tracking-[0.02em] tabular-nums text-[var(--theme-accent-text)]"
              style={DISPLAY}
            >
              {control.n}
            </span>
          </Fragment>
        ))}
        <span className="h-0 min-w-2 flex-1 border-t border-[var(--rule)]" />
        <ArrowRight
          size={16}
          strokeWidth={1.5}
          className="shrink-0 text-[color-mix(in_srgb,var(--theme-ink)_60%,transparent)]"
        />
      </div>
    </div>
  );
}

function ControlLedgerSection() {
  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: GROUND, color: INK }}
    >
      <Grain opacity={0.45} />
      <div className={`relative z-10 ${EDITORIAL_SECTION_CLS}`}>
        <div className="grid grid-cols-[0.85fr_1.15fr] gap-x-14 gap-y-12 max-lg:grid-cols-1 max-lg:gap-y-10">
          <div>
            <SectionMarker section="" label="Controls" />
            <h2
              className={`mt-8 max-w-[13ch] text-[var(--theme-ink)] ${SECTION_TITLE_CLS}`}
              style={DISPLAY}
            >
              Only what your team approves.
            </h2>
            <p className={`mt-6 ${INK_88} ${SECTION_INTRO_CLS}`}>
              MLX starts scoped to your organisation and read-only, with human
              review before business context is published. Each control below is
              part of the product available today, not a roadmap item.
            </p>
          </div>
          <div>
            <GateRail />
            <ol className="mt-10 grid gap-x-10 gap-y-8 sm:grid-cols-2">
              {CONTROLS.map((control) => (
                <li
                  key={control.n}
                  className="grid grid-cols-[3rem_1fr] items-start gap-x-4 border-t border-[color-mix(in_srgb,var(--theme-ink)_18%,transparent)] pt-5"
                >
                  <span
                    className="text-[1.3rem] leading-none font-semibold tracking-[-0.01em] tabular-nums text-[var(--theme-accent-text)]"
                    style={DISPLAY}
                  >
                    {control.n}
                  </span>
                  <div className="min-w-0">
                    <h3
                      className="text-[1.1rem] leading-[1.3] font-semibold tracking-[-0.01em] text-[var(--theme-ink)] md:text-[1.2rem]"
                      style={DISPLAY}
                    >
                      {control.title}
                    </h3>
                    <p
                      className={`mt-2 text-[0.95rem] leading-[1.6] ${INK_88}`}
                    >
                      {control.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================= */
/*  GOV-002 · Data-path diagram                                         */
/* ================================================================= */

const SOURCE_SYSTEMS = [
  { name: "Xero", icon: "xero.svg" },
  { name: "SunSystems", icon: "sunsystems.svg" },
  { name: "Microsoft 365", icon: "microsoft365.svg" },
  { name: "SharePoint", icon: "sharepoint.svg" },
  { name: "Google Drive", icon: "googledrive.svg" },
  { name: "Slack", icon: "slack.png" },
] as const;

const TWIN_LAYERS = [
  ["Evidence", "Read from approved sources"],
  ["Proposal", "Definitions linked to sources"],
  ["Approved version", "Reviewed · recorded · published"],
] as const;

const AI_SURFACES = [
  { name: "MLX", src: "/logo.svg" },
  { name: "ChatGPT", src: "/concepts/source-icons/chatgpt.svg" },
  { name: "Claude", src: "/concepts/source-icons/claude.svg" },
  { name: "Gemini", src: "/concepts/source-icons/gemini.svg" },
] as const;

function PathArrow({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 bg-[var(--theme-ground)] px-3 py-4 text-center md:px-2">
      <ArrowRight
        size={18}
        strokeWidth={1.5}
        aria-hidden="true"
        className="max-md:hidden"
      />
      <ArrowDown
        size={18}
        strokeWidth={1.5}
        aria-hidden="true"
        className="md:hidden"
      />
      <span className={`md:max-w-[7rem] ${INK_80} ${DATA_LABEL_CLS}`}>
        {label}
      </span>
    </div>
  );
}

function DataPathDiagram() {
  return (
    <div className="border border-[var(--rule)]">
      <ExhibitHeader
        left="Exhibit · the data path"
        right="Source systems remain authoritative"
      />
      <div className="grid gap-px bg-[var(--rule)] max-md:grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(6.5rem,auto)_minmax(0,1.15fr)_minmax(6.5rem,auto)_minmax(0,1fr)]">
        <div className="min-w-0 bg-[var(--theme-ground)]">
          <p
            className={`border-b border-dotted border-[var(--rule)] px-4 py-3 ${INK_80} ${DATA_LABEL_CLS}`}
          >
            Your systems
          </p>
          <ul className="grid grid-cols-2 gap-x-3 gap-y-2.5 px-4 py-4 max-sm:grid-cols-1">
            {SOURCE_SYSTEMS.map((source) => (
              <li key={source.name} className="flex min-w-0 items-center gap-2">
                <Image
                  src={`/concepts/source-icons/${source.icon}`}
                  width={22}
                  height={22}
                  alt=""
                  loading="lazy"
                  className="size-[22px] shrink-0 object-contain"
                />
                <span className="min-w-0 text-[0.82rem] leading-[1.25] font-medium">
                  {source.name}
                </span>
              </li>
            ))}
          </ul>
          <p className={`px-4 pb-4 text-[0.78rem] leading-[1.5] ${INK_80}`}>
            Modern and legacy · 18 connectors · no migration
          </p>
        </div>

        <PathArrow label="Scoped, read-only connector" />

        <div className="min-w-0 bg-[var(--theme-ground)]">
          <p
            className={`border-b border-dotted border-[var(--rule)] px-4 py-3 ${INK_80} ${DATA_LABEL_CLS}`}
          >
            The Twin
          </p>
          <dl className="px-4 py-2">
            {TWIN_LAYERS.map(([label, value]) => (
              <div
                key={label}
                className="flex flex-col gap-0.5 border-b border-dotted border-[var(--rule)] py-2.5 last:border-b-0"
              >
                <dt
                  className="text-[0.95rem] font-semibold tracking-[-0.01em]"
                  style={DISPLAY}
                >
                  {label}
                </dt>
                <dd
                  className={`m-0 text-[length:var(--type-data)] font-medium tracking-[0.04em] uppercase ${INK_80}`}
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <PathArrow label="Approved context · versioned" />

        <div className="min-w-0 bg-[var(--theme-ground)]">
          <p
            className={`border-b border-dotted border-[var(--rule)] px-4 py-3 ${INK_80} ${DATA_LABEL_CLS}`}
          >
            Approved AI tools
          </p>
          <ul className="grid grid-cols-2 gap-px bg-[var(--rule-soft)] p-px">
            {AI_SURFACES.map((surface) => (
              <li
                key={surface.name}
                className="flex min-h-[4.5rem] flex-col items-center justify-center gap-2 bg-[var(--theme-ground)] px-2 py-3 text-center"
              >
                <Image
                  src={surface.src}
                  alt=""
                  width={26}
                  height={26}
                  className="size-[26px] object-contain"
                />
                <span className={`${INK_80} ${DATA_LABEL_CLS}`}>
                  {surface.name}
                </span>
              </li>
            ))}
          </ul>
          <p className={`px-4 py-4 text-[0.78rem] leading-[1.5] ${INK_80}`}>
            Configured for your deployment
          </p>
        </div>
      </div>

      {/* Dotted return — the path back is authority, not data. */}
      <div className="flex items-center gap-3 border-t border-dotted border-[var(--rule)] px-4 py-3">
        <ArrowLeft
          size={16}
          strokeWidth={1.5}
          aria-hidden="true"
          className="shrink-0 text-[var(--theme-accent-text)]"
        />
        <span
          aria-hidden="true"
          className="h-0 min-w-4 flex-1 border-t border-dotted border-[var(--rule)]"
        />
        <span
          className={`min-w-0 text-center text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}
        >
          Systems remain authoritative · read-only connection
        </span>
        <span
          aria-hidden="true"
          className="h-0 min-w-4 flex-1 border-t border-dotted border-[var(--rule)] max-sm:hidden"
        />
      </div>
    </div>
  );
}

/* ================================================================= */
/*  GOV-003 · What a governed query does not do                         */
/* ================================================================= */

const BOUNDARY_ROWS = [
  "No browser access",
  "No terminal access",
  "No write access to source systems",
  "No plausible guesses, unsupported questions say so",
] as const;

function BoundaryExhibit() {
  return (
    <div
      className="border border-[var(--rule)]"
      style={{
        backgroundImage:
          "radial-gradient(color-mix(in srgb, var(--theme-accent) 8%, transparent) 1px, transparent 1.6px)",
        backgroundSize: "24px 24px",
      }}
    >
      <ExhibitHeader
        left="Exhibit · a normal governed query"
        right="Product available today"
        dark
      />
      <ul className="m-0 list-none p-0">
        {BOUNDARY_ROWS.map((row) => (
          <li
            key={row}
            className="flex items-start gap-4 border-b border-dotted border-[var(--rule)] px-5 py-4 last:border-b-0 md:px-6"
          >
            <span
              className="mt-[0.2rem] flex size-6 shrink-0 items-center justify-center border border-[color-mix(in_srgb,var(--theme-accent)_55%,transparent)] text-[var(--theme-accent)]"
              aria-hidden="true"
            >
              <X size={14} strokeWidth={1.5} />
            </span>
            <span
              className="text-[1.1rem] leading-[1.35] font-semibold tracking-[-0.015em] text-[var(--theme-ground)] md:text-[1.25rem]"
              style={DISPLAY}
            >
              {row}
            </span>
          </li>
        ))}
      </ul>
      <p
        className={`border-t border-dotted border-[var(--rule)] px-5 py-3 text-[0.85rem] leading-[1.55] md:px-6 ${GROUND_80}`}
      >
        Normal governed queries do not include browser, terminal or write
        access. A regulated deployment still requires approval of the exact
        data and model paths, evidence retention and operating controls.
      </p>
    </div>
  );
}

/* ================================================================= */
/*  GOV-004 · Deployment options                                        */
/* ================================================================= */

const DEPLOYMENT_ROWS = FEATURE_ROWS.filter(
  (row) => row.group === "Twin foundation" || row.group === "Business information",
);

function DeploymentOptions() {
  return (
    <div className="border border-[var(--rule)]">
      <ExhibitHeader
        left="Exhibit · deployment options"
        right="Dedicated · organisation-scoped"
      />
      <div className="grid gap-px bg-[var(--rule)] max-md:grid-cols-1 md:grid-cols-3">
        {TWIN_PLANS.map((plan) => (
          <div key={plan.id} className="min-w-0 bg-[var(--theme-ground)]">
            <div className="border-b border-dotted border-[var(--rule)] px-5 pt-5 pb-4">
              <p className={`text-[var(--theme-accent-text)] ${LABEL_CLS}`}>
                {plan.eyebrow}
              </p>
              <h3
                className="mt-2 text-[1.6rem] leading-[1.05] font-semibold tracking-[-0.025em] text-[var(--theme-ink)]"
                style={DISPLAY}
              >
                {plan.name}
              </h3>
              <p className={`mt-1 text-[0.85rem] ${INK_80}`}>
                {plan.price}
                {plan.cadence ? ` ${plan.cadence}` : ""}
              </p>
            </div>
            <dl className="m-0">
              {DEPLOYMENT_ROWS.map((row) => (
                <div
                  key={row.feature}
                  className="border-b border-dotted border-[var(--rule)] px-5 py-3 last:border-b-0"
                >
                  <dt
                    className={`text-[length:var(--type-label-small)] font-medium leading-[1.4] tracking-[0.14em] uppercase ${INK_80}`}
                  >
                    {row.feature}
                  </dt>
                  <dd className="m-0 mt-1 text-[length:var(--type-data)] font-medium tracking-[0.04em] uppercase">
                    {row[plan.id]}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
      <p
        className={`border-t border-dotted border-[var(--rule)] px-4 py-3 text-[0.8rem] leading-[1.5] ${INK_80}`}
      >
        Twin foundation and context rows from the published pricing table. Exact
        scope is confirmed before activation.
      </p>
    </div>
  );
}

/* ================================================================= */
/*  GOV-005 · Region plate                                              */
/* ================================================================= */

const REGIONS = [
  { id: "eu", label: "EU", text: "EU region" },
  { id: "us", label: "US", text: "US region" },
] as const;

type RegionId = (typeof REGIONS)[number]["id"];

function RegionPlate() {
  const [region, setRegion] = useState<RegionId>("eu");
  const uid = useId();
  const labelId = `${uid}-label`;
  const groupName = `${uid}-region`;
  const selected = REGIONS.find((r) => r.id === region) ?? REGIONS[0];

  return (
    <div className="border border-[var(--rule)]">
      <ExhibitHeader left="Deployment, MLX server" right="EU or US region" />
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        className="px-4 py-4"
      >
        <p id={labelId} className={`${INK_80} ${DATA_LABEL_CLS}`}>
          Choose a region
        </p>
        <div className="mt-2 grid grid-cols-2 gap-px border border-[var(--rule)] bg-[var(--rule)]">
          {REGIONS.map((option) => {
            const active = option.id === region;
            return (
              <label
                key={option.id}
                className={`relative flex min-h-12 cursor-pointer flex-col items-center justify-center gap-0.5 px-3 py-2 transition-colors duration-150 ease-out has-[:focus-visible]:outline has-[:focus-visible]:outline-1 has-[:focus-visible]:-outline-offset-[3px] has-[:focus-visible]:outline-[var(--theme-dark)] motion-reduce:transition-none ${
                  active
                    ? "bg-[var(--theme-surface-dark)] text-[var(--theme-ground)]"
                    : "bg-[var(--theme-ground)] text-[var(--theme-ink)] hover:bg-[color-mix(in_srgb,var(--theme-dark)_7%,transparent)]"
                }`}
              >
                <input
                  type="radio"
                  name={groupName}
                  value={option.id}
                  checked={active}
                  onChange={() => setRegion(option.id)}
                  className="sr-only"
                />
                <span
                  className="text-[1.2rem] leading-none font-semibold tracking-[-0.02em]"
                  style={DISPLAY}
                >
                  {option.label}
                </span>
                <span
                  className={`${DATA_LABEL_CLS} ${active ? GROUND_80 : INK_80}`}
                >
                  {option.text}
                </span>
              </label>
            );
          })}
        </div>
      </div>
      <dl className="m-0">
        <LedgerRow
          label="Server"
          value="Dedicated, organisation-scoped MLX server"
          emphasis
        />
        <LedgerRow label="Region" value={selected.text} emphasis />
        <LedgerRow label="Connectors" value="Read-only · scoped to you" />
        <LedgerRow label="Sources" value="Remain authoritative" />
      </dl>
      <p
        className={`border-t border-dotted border-[var(--rule)] px-4 py-3 text-[0.8rem] leading-[1.5] ${INK_80}`}
      >
        Enterprise: tailored environment scoped with architecture and
        governance.
      </p>
    </div>
  );
}

/* ================================================================= */
/*  GOV-006 · Evidence contract card                                    */
/* ================================================================= */

const RECEIPT_ROWS = [
  ["Approved definition", "Monthly revenue"],
  ["Version", "v2"],
  ["Data current to", "30 Jun 2026"],
  ["Evidence used", "Published business view"],
  ["If unsupported", "Says so · never guesses"],
] as const;

function EvidenceContractCard() {
  return (
    <div>
      <h3
        className="max-w-[16ch] text-[1.8rem] leading-[1.08] font-semibold tracking-[-0.025em] text-[var(--theme-ink)] md:text-[2.1rem]"
        style={DISPLAY}
      >
        Every governed answer carries a receipt.
      </h3>
      <p className={`mt-4 max-w-[44ch] text-[0.98rem] leading-[1.6] ${INK_88}`}>
        Each governed answer identifies the approved definition and version it
        used, with governed evidence references. Configured products can also
        expose source freshness.
      </p>
      <div className="mt-7 border border-[var(--rule)]">
        <ExhibitHeader
          left="Exhibit · the receipt"
          right="Illustrative specimen"
        />
        <div className="px-4 py-4">
          <p className={`${INK_80} ${DATA_LABEL_CLS}`}>Question</p>
          <p
            className="mt-2 text-[1.05rem] font-semibold tracking-[-0.01em]"
            style={DISPLAY}
          >
            “What was revenue last quarter?”
          </p>
          <p className={`mt-4 ${INK_80} ${DATA_LABEL_CLS}`}>
            An approved Twin answer
          </p>
          <p
            className="mt-2 text-[1.35rem] leading-[1.2] font-semibold tracking-[-0.02em]"
            style={DISPLAY}
          >
            £1,243,908 · Apr–Jun 2026
          </p>
        </div>
        <dl className="m-0">
          {RECEIPT_ROWS.map(([label, value]) => (
            <LedgerRow key={label} label={label} value={value} emphasis />
          ))}
        </dl>
      </div>
    </div>
  );
}

/* ================================================================= */
/*  GOV-007 · Model route card                                          */
/* ================================================================= */

const MODEL_PROVIDERS = [
  { name: "ChatGPT", src: "/concepts/source-icons/chatgpt.svg" },
  { name: "Claude", src: "/concepts/source-icons/claude.svg" },
  { name: "Gemini", src: "/concepts/source-icons/gemini.svg" },
] as const;

const ROUTE_DOORS: { name: string; src?: string; glyph?: string; meta: string }[] = [
  { name: "Your Twin", src: "/logo.svg", meta: "Twin chat" },
  { name: "ChatGPT", src: "/concepts/source-icons/chatgpt.svg", meta: "over MCP" },
  { name: "Claude", src: "/concepts/source-icons/claude.svg", meta: "over MCP" },
  { name: "Gemini", src: "/concepts/source-icons/gemini.svg", meta: "over MCP" },
  { name: "Copilot", src: "/concepts/source-icons/copilot.svg", meta: "agent via MCP" },
  { name: "CLI", glyph: "$_", meta: "twin query" },
  { name: "SDK · API", glyph: "{ }", meta: "your code" },
];

export function ModelRouteCard({
  chrome = true,
  doors = false,
}: {
  chrome?: boolean;
  doors?: boolean;
} = {}) {
  const cells = doors ? ROUTE_DOORS : MODEL_PROVIDERS.map((provider) => ({ ...provider, glyph: undefined, meta: undefined }));
  const colsClass = doors ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-7" : "grid-cols-3";
  return (
    <div className={chrome ? "border border-[var(--rule)]" : undefined}>
      {chrome ? (
        <ExhibitHeader
          left="Exhibit · model route"
          right="Keep the map · choose the intelligence"
        />
      ) : null}
      <div className={chrome ? "grid gap-x-12 gap-y-10 px-5 py-8 max-lg:grid-cols-1 md:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center" : "grid"}>
        {chrome ? (
        <div>
          <p className={`text-[var(--theme-accent-text)] ${LABEL_CLS}`}>
            Model choice
          </p>
          <h3
            className="mt-4 max-w-[16ch] text-[1.8rem] leading-[1.08] font-semibold tracking-[-0.025em] text-[var(--theme-ink)] md:text-[2.1rem]"
            style={DISPLAY}
          >
            Context stays separate from the provider.
          </h3>
          <p
            className={`mt-5 max-w-[46ch] text-[0.98rem] leading-[1.6] ${INK_88}`}
          >
            Use stronger models for demanding work, lower-cost models for
            routine tasks or change providers without rebuilding the Twin&apos;s
            business context. Material changes are tested before rollout.
          </p>
        </div>
        ) : null}

        <div>
          <div className="flex min-h-[3.25rem] items-center justify-between gap-4 bg-[var(--theme-surface-dark)] px-4 py-3 text-[var(--theme-ground)]">
            <span className={`${GROUND_80} ${DATA_LABEL_CLS}`}>
              Your business knowledge
            </span>
            <strong className={`text-[var(--theme-accent)] ${DATA_LABEL_CLS}`}>
              v2
            </strong>
          </div>
          {/* One drop per route, all from the same context. */}
          <div aria-hidden="true" className={`grid max-md:hidden ${doors ? "grid-cols-7" : "grid-cols-3"}`}>
            {cells.map((provider) => (
              <div
                key={provider.name}
                className="flex h-12 flex-col items-center"
              >
                <div className="h-8 border-l border-[var(--rule)]" />
                <div className="size-2 rotate-45 border-r border-b border-[color-mix(in_srgb,var(--theme-ink)_60%,transparent)]" />
              </div>
            ))}
          </div>
          <ul
            aria-label="Configured model providers"
            className={`m-0 grid list-none gap-px bg-[var(--rule)] p-px ${colsClass}`}
          >
            {cells.map((cell) => (
              <li
                key={cell.name}
                className="flex min-h-[5.5rem] flex-col items-center justify-center gap-2 bg-[var(--theme-ground)] px-2 py-4 text-center"
              >
                {cell.name === "Copilot" ? (
                  <span aria-hidden="true" className="size-[30px]" style={{
                    background: "conic-gradient(from 30deg,#21b9ba,#498bef,#9754e8,#d953a3,#efa057,#7bbb63,#21b9ba)",
                    mask: "url('/concepts/source-icons/copilot.svg') center/contain no-repeat",
                  }} />
                ) : cell.src ? (
                  <Image
                    src={cell.src}
                    alt=""
                    width={30}
                    height={30}
                    className="size-[30px] object-contain"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className={`flex h-[30px] items-center font-mono text-[0.95rem] font-semibold ${INK_80}`}
                  >
                    {cell.glyph}
                  </span>
                )}
                <span className={`${INK_80} ${DATA_LABEL_CLS}`}>{cell.name}</span>
                {"meta" in cell && cell.meta ? (
                  <span className={`-mt-1 text-[0.68rem] leading-[1.3] ${INK_MUTED} ${doors ? "" : "hidden"}`}>
                    {cell.meta}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          <p className={`mt-3 text-[0.78rem] leading-[1.5] ${INK_80}`}>
            {doors
              ? "Connected tools use the same Twin, with your organisation’s access rules. Answers from the checked Twin show the sources and version used."
              : "When a workflow uses an external provider, the context for that request follows the provider path approved for your deployment."}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ================================================================= */
/*  GOV-008 · Regulated-deployment checklist                            */
/* ================================================================= */

const REGULATED_CHECKS = [
  {
    title: "Exact data paths",
    body: "Which systems are connected, read-only, and which evidence the Twin may read.",
  },
  {
    title: "Exact model paths",
    body: "Which configured providers a workflow may use, and the path its request context follows.",
  },
  {
    title: "Evidence retention",
    body: "How the evidence behind governed answers is retained for your deployment.",
  },
  {
    title: "Operating controls",
    body: "How the configured target is operated and how changes are made once live.",
  },
  {
    title: "Human review gate",
    body: "Who reviews proposed business context before it is published.",
  },
  {
    title: "Read-only start",
    body: "The first activation connects priority systems read-only. Source systems remain authoritative.",
  },
] as const;

function RegulatedChecklist() {
  return (
    <div>
      <SectionMarker section="" label="Regulated deployments" />
      <div className="mt-6 grid gap-x-14 gap-y-8 max-lg:grid-cols-1 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <h3
            className="max-w-[14ch] text-[1.8rem] leading-[1.08] font-semibold tracking-[-0.025em] text-[var(--theme-ink)] md:text-[2.1rem]"
            style={DISPLAY}
          >
            Approved before go-live.
          </h3>
          <p
            className={`mt-4 max-w-[42ch] text-[0.98rem] leading-[1.6] ${INK_88}`}
          >
            MLX starts scoped to your organisation and read-only, with human
            review before business context is published. A regulated deployment
            still requires approval of the exact data and model paths, evidence
            retention and operating controls.
          </p>
          <p className={`mt-5 text-[0.8rem] leading-[1.5] ${INK_80}`}>
            From the buyer question “We&apos;re regulated. Can we actually use
            this?”
          </p>
        </div>
        <div className="border border-[var(--rule)]">
          <ExhibitHeader
            left="Checklist, approved before go-live"
            right="Scoped per deployment"
          />
          <ul className="m-0 list-none p-0">
            {REGULATED_CHECKS.map((check) => (
              <li
                key={check.title}
                className="grid grid-cols-[1.5rem_minmax(0,1fr)] items-start gap-x-4 border-b border-dotted border-[var(--rule)] px-4 py-3.5 last:border-b-0"
              >
                <Square
                  size={18}
                  strokeWidth={1.5}
                  aria-hidden="true"
                  className="mt-[0.15rem] text-[var(--theme-accent-text)]"
                />
                <div className="min-w-0">
                  <p
                    className="text-[1rem] leading-[1.3] font-semibold tracking-[-0.01em]"
                    style={DISPLAY}
                  >
                    {check.title}
                  </p>
                  <p className={`mt-1 text-[0.88rem] leading-[1.55] ${INK_88}`}>
                    {check.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ================================================================= */
/*  GOV-009 · Five gates                                                */
/* ================================================================= */

const FIVE_GATES = [
  {
    n: "01",
    title: "Scope",
    body: "The query runs against a target scoped to your organisation, over read-only connectors to the systems you approved.",
  },
  {
    n: "02",
    title: "Evidence",
    body: "The answer is drawn from governed evidence the Twin has read, not from whatever a model could reach.",
  },
  {
    n: "03",
    title: "Approved definition",
    body: "The calculation is one your team reviewed and approved, not one improvised for this chat.",
  },
  {
    n: "04",
    title: "Version & freshness",
    body: "The answer names the approved version it used, and configured products can expose how current the data is.",
  },
  {
    n: "05",
    title: "Support check",
    body: "If the approved product cannot support the question, the Twin returns “not supported” instead of a plausible guess.",
  },
] as const;

function FiveGatesSection() {
  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: GROUND, color: INK }}
    >
      <Grain opacity={0.45} />
      <div className={`relative z-10 ${EDITORIAL_SECTION_CLS}`}>
        <SectionMarker section="" label="Five gates" />
        <div className="mt-10 grid grid-cols-[1.25fr_0.75fr] gap-12 max-lg:grid-cols-1 max-lg:gap-6">
          <h2
            className={`max-w-[16ch] text-[var(--theme-ink)] ${SECTION_TITLE_CLS}`}
            style={DISPLAY}
          >
            Five gates between a guess and a governed answer.
          </h2>
          <p className={`max-w-[35rem] ${INK_88} ${SECTION_INTRO_CLS}`}>
            A number is not true because a model said it. A governed answer
            passes each of these checks, and a pass at one is not evidence of a
            pass at another.
          </p>
        </div>

        <ol className="mt-14 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-5 md:mt-16">
          {FIVE_GATES.map((gate) => (
            <li
              key={gate.n}
              className="border-t border-[color-mix(in_srgb,var(--theme-ink)_18%,transparent)] pt-5"
            >
              <div className="flex items-center gap-3">
                <span
                  className="text-[1.3rem] leading-none font-semibold tracking-[-0.01em] tabular-nums text-[var(--theme-accent-text)]"
                  style={DISPLAY}
                >
                  {gate.n}
                </span>
                <span
                  aria-hidden="true"
                  className="h-0 flex-1 border-t border-dotted border-[var(--rule)] max-sm:hidden"
                />
              </div>
              <h3
                className="mt-6 text-[1.15rem] leading-[1.3] font-semibold tracking-[-0.01em] text-[var(--theme-ink)] md:text-[1.2rem]"
                style={DISPLAY}
              >
                {gate.title}
              </h3>
              <p className={`mt-2 text-[0.92rem] leading-[1.6] ${INK_88}`}>
                {gate.body}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-12 flex flex-wrap items-baseline gap-x-4 gap-y-2 border-t border-[var(--rule)] pt-5">
          <span className={`${INK_80} ${DATA_LABEL_CLS}`}>
            From the MLX blog
          </span>
          <a
            href="/blog/five-gates-to-a-governed-answer"
            className="text-[0.95rem] font-semibold text-[var(--theme-accent-text)] underline-offset-4 hover:underline"
          >
            Five Gates Between a Guess and a Governed Answer →
          </a>
        </div>
      </div>
    </section>
  );
}

/* ================================================================= */
/*  GOV-010 · Governance loop band                                      */
/* ================================================================= */

const LOOP_STAGES = [
  {
    n: "01",
    title: "Connect",
    body: "Scoped, read-only connectors bring relevant evidence from approved source systems into the Twin while those systems remain authoritative.",
  },
  {
    n: "02",
    title: "Derive",
    body: "MLX proposes definitions and calculations, links them to their sources and records each approved version.",
  },
  {
    n: "03",
    title: "Approve",
    body: "Your people review the proposals. What they approve becomes durable business context that approved AI can use.",
  },
  {
    n: "04",
    title: "Compound",
    body: "Twin data refreshes on a schedule (daily on Core, higher-frequency on Scale, cadence scoped per source on Enterprise) and each approved version stays recorded.",
  },
] as const;

/** Dark override for the brand engraving — the StackExhibit recipe, with
 *  `--dark` pointed at the accent so the dial's claret marks (node numbers,
 *  the leading arc, arrowheads) stay visible on the deep surface. */
const LOOP_EXTRA_CSS = `
.concepts-brand.gov-loop-dark{
  --dark:var(--theme-accent);
  --ground:var(--theme-surface-dark);
  --ink:var(--theme-ground);
  --dot:color-mix(in srgb,var(--theme-accent) 8%,transparent);
  background-color:var(--theme-surface-dark);
  background-image:radial-gradient(var(--dot) 1px,transparent 1.6px);
  color:var(--theme-accent);
}
.concepts-brand.gov-loop-dark .gov-loop-figure svg{
  display:block;
  width:100%;
  height:auto;
}
`;

function GovernanceLoopSection() {
  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: SURFACE_DARK, color: GROUND }}
    >
      <Grain opacity={0.38} />
      <div className={`relative z-10 ${EDITORIAL_SECTION_CLS}`}>
        <SectionMarker section="" label="Governance loop" dark />
        <div className="mt-10 grid grid-cols-[1.25fr_0.75fr] gap-12 max-lg:grid-cols-1 max-lg:gap-6">
          <h2
            className={`max-w-[14ch] text-[var(--theme-ground)] ${SECTION_TITLE_CLS}`}
            style={DISPLAY}
          >
            Governance is a loop, not a sign-off.
          </h2>
          <p className={`max-w-[35rem] ${GROUND_88} ${SECTION_INTRO_CLS}`}>
            Business context is not approved once and forgotten. The Twin
            refreshes on a schedule, each approved version is recorded, and your
            people decide what becomes trusted context.
          </p>
        </div>

        {/* Outer wrapper carries the gap: BRAND_CSS zeroes `.concepts-brand`
            margins, so a Tailwind mt-* on the panel itself would be lost. */}
        <div className="mt-14 md:mt-16">
          <div className="concepts-brand gov-loop-dark relative overflow-hidden rounded-[3px] border border-[color-mix(in_srgb,var(--theme-accent)_24%,transparent)]">
            <style>{BRAND_CSS + LOOP_EXTRA_CSS}</style>
            <div
              aria-hidden="true"
              className="h-1 w-full bg-[var(--theme-accent)]"
            />
            <div className="flex min-h-[3.25rem] items-center justify-between gap-4 border-b border-dotted border-[color-mix(in_srgb,var(--theme-accent)_28%,transparent)] px-5 py-3.5 max-md:flex-col max-md:items-start max-md:gap-1.5 md:px-7">
              <span className={`${GROUND_80} ${DATA_LABEL_CLS}`}>
                Exhibit · the operating loop
              </span>
              <strong
                className={`text-[var(--theme-accent)] ${DATA_LABEL_CLS}`}
              >
                Connect · derive · approve · compound
              </strong>
            </div>

            <div className="grid items-center gap-x-12 gap-y-10 px-5 py-10 max-lg:grid-cols-1 md:px-10 md:py-12 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
              <div className="gov-loop-figure mx-auto w-full max-w-[26rem]">
                <Engraving data={BrandFigures.loopDial} />
              </div>
              <ol className="m-0 grid list-none gap-x-8 gap-y-7 p-0 sm:grid-cols-2">
                {LOOP_STAGES.map((stage) => (
                  <li
                    key={stage.n}
                    className="grid grid-cols-[3rem_1fr] items-start gap-x-4 border-t border-[color-mix(in_srgb,var(--theme-ground)_22%,transparent)] pt-5"
                  >
                    <span
                      className="text-[1.3rem] leading-none font-semibold tracking-[-0.01em] tabular-nums text-[var(--theme-accent)]"
                      style={DISPLAY}
                    >
                      {stage.n}
                    </span>
                    <div className="min-w-0">
                      <h3
                        className="text-[1.15rem] leading-[1.3] font-semibold tracking-[-0.01em] text-[var(--theme-ground)] md:text-[1.25rem]"
                        style={DISPLAY}
                      >
                        {stage.title}
                      </h3>
                      <p
                        className={`mt-2 text-[0.92rem] leading-[1.6] ${GROUND_88}`}
                      >
                        {stage.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================= */
/*  Group                                                               */
/* ================================================================= */

export const GOVERNANCE_GROUP: ConceptGroup = {
  id: "governance",
  prefix: "GOV",
  title: "Governance & controls",
  blurb:
    "How MLX states what it will and will not do: the controls a buyer can check, the data path, deployment and region choices, and the receipt every governed answer carries. Every claim here is drawn from the product available today.",
  specs: [
    {
      id: "GOV-001",
      title: "Control ledger with gate diagram",
      note: "Numbered control ledger with a hairline gate rail, the Twin1 access-control pattern re-expressed with MLX's real controls from the buyer questions.",
      surface: "ground",
      width: "full",
      render: () => <ControlLedgerSection />,
    },
    {
      id: "GOV-002",
      title: "Data-path diagram",
      note: "Your systems → scoped read-only connector → the Twin → approved AI tools, with a dotted return marking that source systems remain authoritative over a read-only connection.",
      surface: "ground",
      width: "contained",
      render: () => <DataPathDiagram />,
    },
    {
      id: "GOV-003",
      title: "What a governed query does not do",
      note: "Dark exhibit listing the boundary of a normal governed query (no browser, terminal or write access, no plausible guesses) from the regulated-deployment FAQ.",
      surface: "dark",
      width: "contained",
      render: () => <BoundaryExhibit />,
    },
    {
      id: "GOV-004",
      title: "Deployment options",
      note: "Core / Scale / Enterprise columns built from the Twin foundation and Context rows of the pricing table; eyebrows and prices from TWIN_PLANS.",
      surface: "ground",
      width: "contained",
      render: () => <DeploymentOptions />,
    },
    {
      id: "GOV-005",
      title: "Region plate",
      note: "Compact residency chooser, EU or US region on a dedicated, organisation-scoped MLX server; Enterprise footnote from pricing data.",
      surface: "ground",
      width: "narrow",
      render: () => <RegionPlate />,
    },
    {
      id: "GOV-006",
      title: "Evidence contract card",
      note: "The receipt a governed answer carries (approved definition, version, freshness, evidence and unsupported handling) using the home's illustrative specimen.",
      surface: "ground",
      width: "narrow",
      render: () => <EvidenceContractCard />,
    },
    {
      id: "GOV-007",
      title: "Model route card",
      note: "One approved business context bar feeding three configured providers; copy from the model-choice benefit and FAQ.",
      surface: "ground",
      width: "contained",
      render: () => <ModelRouteCard />,
    },
    {
      id: "GOV-008",
      title: "Regulated-deployment checklist",
      note: "Square-checkbox ledger of what is approved before go-live, expanded from the 'We're regulated' buyer question.",
      surface: "ground",
      width: "contained",
      render: () => <RegulatedChecklist />,
    },
    {
      id: "GOV-009",
      title: "Five gates",
      note: "Horizontal 01–05 stepper (scope, evidence, approved definition, version and freshness, support check) linking to the MLX blog essay of the same name.",
      surface: "ground",
      width: "full",
      render: () => <FiveGatesSection />,
    },
    {
      id: "GOV-010",
      title: "Governance loop band",
      note: "Dark band with the loop-dial engraving in a .concepts-brand panel (StackExhibit recipe) and four captions matching its dial: connect, derive, approve, compound.",
      surface: "dark",
      width: "full",
      render: () => <GovernanceLoopSection />,
    },
    {
      id: "GOV-011",
      title: "The inbox, as the Twin asks",
      note: "The governed surface plane as a product moment, in the product's inbox look: three plain-language asks (link two records, use a definition, run a job) with the product's three answers, and the undo rule in the footer. No kind labels, versions, confidences or policy rows. Serves the landing's Govern band.",
      surface: "ground",
      width: "contained",
      interaction: "static",
      render: () => <ProposalInboxExhibit />,
    },
  ],
};
